<template>
  <section class="breakdown-section" :class="{ wide }">
    <h6 class="breakdown-title text-uppercase text-body-tertiary mb-2">
      {{ title }}
    </h6>
    <slot>
      <ul class="list-unstyled mb-0">
        <li
          v-for="entry in entries"
          :key="entry.label"
          class="breakdown-row"
          :title="`${entry.label}: ${entry.mwh} MWh (${percent(entry)}%)`"
        >
          <div class="d-flex justify-content-between gap-2">
            <span class="text-truncate">{{ entry.label }}</span>
            <span class="text-nowrap tabular-nums">
              {{ entry.mwh }} MWh
              <span class="text-body-tertiary ms-1">{{ percent(entry) }}%</span>
            </span>
          </div>
          <div class="share-track" aria-hidden="true">
            <div class="share-bar" :style="{ width: `${barWidth(entry)}%` }" />
          </div>
        </li>
      </ul>
    </slot>
  </section>
</template>

<script setup lang="ts">
/** One row of a breakdown: a group and the MWh it holds out of the section's `total`. */
export interface BreakdownEntry {
  label: string;
  mwh: number;
}

/**
 * One card in the selection's breakdown grid. Given `entries`, each row shows its MWh, its share
 * of `total` and a thin share bar; the default slot replaces the rows (e.g. a table), and `wide`
 * lets such content span two grid columns.
 */
const props = withDefaults(
  defineProps<{
    title: string;
    entries?: BreakdownEntry[];
    total?: number;
    wide?: boolean;
  }>(),
  { entries: () => [], total: 0, wide: false }
);

const share = (entry: BreakdownEntry): number =>
  props.total > 0 ? (entry.mwh / props.total) * 100 : 0;

const percent = (entry: BreakdownEntry): string => share(entry).toFixed(1);

// A non-zero share stays visible as a sliver rather than vanishing below a pixel.
const barWidth = (entry: BreakdownEntry): number =>
  entry.mwh > 0 ? Math.max(share(entry), 1) : 0;
</script>

<style lang="scss" scoped>
.breakdown-section {
  padding: 0.75rem 1rem;
  border: 1px solid var(--phoenix-border-color);
  border-radius: 0.5rem;
  min-width: 0;

  &.wide {
    grid-column: span 2;
  }
}

// On a one-column grid there is nothing to span.
@media (max-width: 575.98px) {
  .breakdown-section.wide {
    grid-column: auto;
  }
}

.breakdown-title {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
}

.breakdown-row {
  font-size: 0.8125rem;

  & + & {
    margin-top: 0.5rem;
  }
}

.tabular-nums {
  font-variant-numeric: tabular-nums;
}

.share-track {
  height: 4px;
  margin-top: 0.25rem;
  border-radius: 2px;
  background-color: var(--phoenix-secondary-bg);
}

.share-bar {
  height: 100%;
  border-radius: 2px;
  background-color: var(--phoenix-primary);
}
</style>
